import React from "react";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";

const progressRingVariants = cva(
  "relative inline-flex items-center justify-center rounded-full",
  {
    variants: {
      size: {
        sm: "w-16 h-16",
        md: "w-24 h-24",
        lg: "w-32 h-32",
      },
      variant: {
        default: "bg-primary/10",
        success: "bg-green-500/10",
        warning: "bg-yellow-500/10", 
        danger: "bg-red-500/10",
        info: "bg-blue-500/10",
      }
    },
    defaultVariants: {
      size: "md",
      variant: "default",
    },
  }
);

export interface ProgressRingProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number;
  max?: number;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "success" | "warning" | "danger" | "info";
  strokeWidth?: number;
  showValue?: boolean;
  valuePrefix?: string;
  valueSuffix?: string;
  animated?: boolean;
  label?: string;
}

export function ProgressRing({
  value,
  max = 100,
  size = "md",
  variant = "default",
  strokeWidth = 4,
  showValue = true,
  valuePrefix = "",
  valueSuffix = "%",
  animated = true,
  label,
  className,
  children,
  ...props
}: ProgressRingProps) {
  const normalizedValue = Math.min(Math.max(0, value), max);
  const percentage = (normalizedValue / max) * 100;
  
  // SVG parameters
  const radius = size === "sm" ? 30 : size === "md" ? 45 : 60;
  const diameter = radius * 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  // Animation class
  const animationClass = animated ? "transition-all duration-1000 ease-in-out" : "";
  
  // Color variants for stroke
  const getStrokeColor = () => {
    switch(variant) {
      case "success": return "stroke-green-500";
      case "warning": return "stroke-yellow-500";
      case "danger": return "stroke-red-500";
      case "info": return "stroke-blue-500";
      default: return "stroke-primary";
    }
  };
  
  // Text size based on ring size
  const getTextSize = () => {
    switch(size) {
      case "sm": return "text-lg";
      case "lg": return "text-3xl";
      default: return "text-2xl";
    }
  };
  
  const getLabelSize = () => {
    switch(size) {
      case "sm": return "text-xs";
      case "lg": return "text-sm";
      default: return "text-xs";
    }
  };

  return (
    <div
      className={cn(progressRingVariants({ size, variant }), className)}
      {...props}
    >
      <svg
        className="absolute inset-0"
        viewBox={`0 0 ${diameter} ${diameter}`}
        width="100%"
        height="100%"
      >
        {/* Background circle */}
        <circle
          className="text-muted-foreground/20 fill-none"
          cx={radius}
          cy={radius}
          r={radius - strokeWidth / 2}
          strokeWidth={strokeWidth}
          stroke="currentColor"
        />
        
        {/* Progress circle */}
        <circle
          className={cn("fill-none", getStrokeColor(), animationClass)}
          cx={radius}
          cy={radius}
          r={radius - strokeWidth / 2}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${radius} ${radius})`}
        />
      </svg>
      
      {/* Display value in the center if enabled */}
      {showValue && (
        <div className="relative flex flex-col items-center justify-center">
          <span className={cn("font-semibold", getTextSize())}>
            {valuePrefix}{Math.round(normalizedValue)}{valueSuffix}
          </span>
          {label && (
            <span className={cn("text-muted-foreground mt-1", getLabelSize())}>
              {label}
            </span>
          )}
        </div>
      )}
      
      {children}
    </div>
  );
}